import type { Country } from "../Country.js";
import { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { ProductForCustomer } from "../Product.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import type { SubscriptionParamsForPricing } from "../Subscription.js";
import type { SubscriptionDeliveryCount } from "../SubscriptionDeliveryCount.js";
import { SubscriptionKind } from "../SubscriptionKind.js";
import { SubscriptionSelection } from "../SubscriptionSelection.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { getFathersChoicePrice } from "../getFathersChoicePrice.js";
import { getMothersBlendPrice } from "../getMothersBlendPrice.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { convertToCurrency } from "./convertToCurrency.js";
import { findSubcategoryOfSubscriptionVariant } from "./findSubcategoryOfSubscriptionVariant.js";
import { shouldApplyTax } from "./shouldApplyTax.js";
import { subscriptionDeliveryCountToNumberMap } from "./subscriptionDeliveryCountToNumberMap.js";
import { subscriptionFrequencyToWeeksMap } from "./subscriptionFrequencyToWeeksMap.js";
import { subscriptionSizeToAmountMap } from "./subscriptionSizeToAmountMap.js";
import { subscriptionSizeToWeightMap } from "./subscriptionSizeToWeightMap.js";

export function calculatePriceOfSubscriptionWithoutDiscounts({
	subcategories,
	destinationCountry,
	subscription,
	mothersBlendProduct,
	cafeinaProduct,
	currency,
	vatType,
	exchangeRates,
	mothersBlendConstantPriceEnabled,
	deliveryCount,
}: {
	subcategories: SubcategoryForCustomer[];
	destinationCountry: Country | "outsideEu";
	subscription: SubscriptionParamsForPricing;
	mothersBlendProduct: ProductForCustomer;
	cafeinaProduct: ProductForCustomer;
	currency: Currency;
	vatType: EconomicSubjectVatType;
	exchangeRates: ExchangeRate[];
	mothersBlendConstantPriceEnabled: boolean;
	deliveryCount: SubscriptionDeliveryCount | undefined;
}): TaxedPrice {
	const subcategory = findSubcategoryOfSubscriptionVariant(subcategories, subscription.variant, true);
	const amount = subscriptionSizeToAmountMap[subscription.size];
	const isGift = subscription.kind === SubscriptionKind.Gift && deliveryCount !== undefined;
	const deliveryCountNumber = isGift ? subscriptionDeliveryCountToNumberMap[deliveryCount] * amount : 1;

	const taxRate = shouldApplyTax(destinationCountry, vatType)
		? subcategory.vatRates.find(({ country }) => country === destinationCountry)?.rate
		: 0;

	if (taxRate === undefined) {
		throw new Error(`Tax rate not found for country ${destinationCountry}`);
	}

	let priceWithoutTax: number;
	const constantPriceFrequency = isGift ? undefined : subscription.frequency;

	if (subscription.selection === SubscriptionSelection.FathersChoice) {
		const priceWithoutTaxCzk = getFathersChoicePrice(subscription.size, constantPriceFrequency);
		priceWithoutTax = convertToCurrency(priceWithoutTaxCzk, Currency.CZK, currency, exchangeRates);
		priceWithoutTax *= deliveryCountNumber;
	} else if (subscription.selection === SubscriptionSelection.MothersBlend && mothersBlendConstantPriceEnabled) {
		const priceWithoutTaxCzk = getMothersBlendPrice(subscription.size, constantPriceFrequency);
		priceWithoutTax = convertToCurrency(priceWithoutTaxCzk, Currency.CZK, currency, exchangeRates);
		priceWithoutTax *= deliveryCountNumber;
	} else {
		const weight = subscriptionSizeToWeightMap[subscription.size];
		const product = {
			[SubscriptionSelection.MothersBlend]: mothersBlendProduct,
			[SubscriptionSelection.Cafeina]: cafeinaProduct,
		}[subscription.selection];
		const variant = product.variants.find(({ weightInGrams }) => weightInGrams === weight);
		const taxedPrice = variant?.prices[currency][taxRate]?.price;
		if (!taxedPrice) {
			throw new Error("Price not found.");
		}
		if (isGift) {
			priceWithoutTax = taxedPrice.withoutTax * deliveryCountNumber;
		} else {
			const weeks = subscriptionFrequencyToWeeksMap[subscription.frequency];
			priceWithoutTax = ((365 / (weeks * 7)) * taxedPrice.withoutTax * amount) / 12;
		}
	}

	return {
		taxRate,
		withTax: calculatePriceWithTax(priceWithoutTax, taxRate),
		withoutTax: priceWithoutTax,
	};
}
